import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBooking } from '../../context/BookingContext';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import { format } from 'date-fns';
import { BrandLogo } from '../BrandLogo';

const Invoice = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const { bookingHistory } = useBooking();

  const booking = bookingHistory.find(b => b.id === bookingId);

  if (!booking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center">
          <h3 className="text-xl font-semibold mb-2">Invoice Not Found</h3>
          <Button onClick={() => navigate('/user/history')}>Go Back</Button>
        </Card>
      </div>
    );
  }

  const invoice = booking.invoice || {
    baseAmount: booking.amount,
    gst: Math.round(booking.amount * 0.18),
    total: booking.amount + Math.round(booking.amount * 0.18)
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10 print:hidden">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/user/history')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-2xl font-bold">Invoice</h1>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Card className="p-8">
          {/* Company Header */}
          <div className="border-b pb-6 mb-6">
            <div className="flex justify-between items-start">
              <div>
                <BrandLogo variant="compact" className="h-9 w-auto mb-2" />
                <p className="text-muted-foreground">Professional Driver Services</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Invoice #{booking.id.toUpperCase()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Invoice Date</p>
                <p className="font-semibold">
                  {format(new Date(booking.createdAt), 'MMM dd, yyyy')}
                </p>
              </div>
            </div>
          </div>

          {/* Customer & Driver Details */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground mb-2">CUSTOMER DETAILS</h3>
              <p className="font-semibold">John Doe</p>
              <p className="text-sm text-muted-foreground">+91 99999 88888</p>
            </div>
            <div>
              <h3 className="font-semibold text-sm text-muted-foreground mb-2">DRIVER DETAILS</h3>
              <p className="font-semibold">{booking.driverName || 'Driver'}</p>
              <p className="text-sm text-muted-foreground">{booking.driverPhone || 'N/A'}</p>
              <p className="text-sm text-muted-foreground">Professional Driver</p>
            </div>
          </div>

          {/* Trip Details */}
          <div className="mb-6">
            <h3 className="font-semibold text-sm text-muted-foreground mb-3">TRIP DETAILS</h3>
            <div className="bg-background rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pickup Location:</span>
                <span className="font-medium">{booking.pickupLocation || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Start Time:</span>
                <span className="font-medium">
                  {format(new Date(booking.createdAt), 'MMM dd, yyyy hh:mm a')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">End Time:</span>
                <span className="font-medium">
                  {format(new Date(booking.endTime), 'MMM dd, yyyy hh:mm a')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration:</span>
                <span className="font-medium">{booking.hours} Hour(s)</span>
              </div>
            </div>
          </div>

          {/* Billing Details */}
          <div className="border-t pt-6">
            <h3 className="font-semibold text-sm text-muted-foreground mb-4">BILLING DETAILS</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-lg">
                <span>Base Fare ({booking.hours} Hour(s))</span>
                <span>₹{invoice.baseAmount}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>GST (18%)</span>
                <span>₹{invoice.gst}</span>
              </div>
              <div className="border-t pt-3 flex justify-between text-2xl font-bold">
                <span>Total Amount</span>
                <span>₹{invoice.total}</span>
              </div>
            </div>
          </div>

          {/* Payment Status */}
          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 font-semibold">✓ Payment Completed</p>
            <p className="text-sm text-green-700">Paid via Wallet on {format(new Date(booking.endTime), 'MMM dd, yyyy')}</p>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t text-center text-sm text-muted-foreground">
            <p>Thank you for choosing BookMyDriver!</p>
            <p className="mt-2">For support, contact: support@bookmydriver.com | +91 12345 67890</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Invoice;
